import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import type { FeatureInfo, PlanAdmin, PlanPayload } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import {
  Alert,
  Button,
  Card,
  CheckboxField,
  FormActions,
  Grid,
  PageHeader,
  SelectField,
  Stack,
  TextField,
  useToast,
} from '../components/ui'
import type { SelectOption } from '../components/ui'
import { MODULES, nomFormule } from './formules'

/** Un champ numérique facultatif : vide veut dire « sans limite », jamais zéro. */
function entierOuNul(saisie: string): number | null {
  const propre = saisie.trim().replace(',', '.')
  return propre === '' ? null : Number(propre)
}

function texte(valeur: number | null | undefined): string {
  return valeur === null || valeur === undefined ? '' : String(valeur)
}

/**
 * Créer ou modifier une formule : son prix, ses plafonds, ses activités et ses
 * fonctionnalités.
 *
 * LE CODE SE CHOISIT UNE FOIS. Il est l'identifiant que reçoivent la création de client et
 * le changement de formule ; la base le fige après création, et l'écran le montre en
 * lecture seule pour ne pas laisser croire qu'on peut le renommer.
 *
 * UN PLAFOND VIDE VEUT DIRE « SANS LIMITE ». Le mot est écrit sous chaque champ, parce que
 * la tentation est de saisir 0 pour « illimité » — ce que la base refuse, à juste titre :
 * zéro n'autoriserait rien.
 *
 * CHANGER LE PRIX NE TOUCHE AUCUN CLIENT EN PLACE. Un abonnement recopie son prix à la
 * souscription ; l'avertissement le dit au moment de modifier, pas après.
 */
export function PlanEditorPage() {
  const { id } = useParams()
  const creation = id === undefined
  const { t, language, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const navigate = useNavigate()

  const token = session?.token ?? ''

  const [catalogue, setCatalogue] = useState<FeatureInfo[]>([])
  const [existante, setExistante] = useState<PlanAdmin | null>(null)
  const [chargement, setChargement] = useState(true)
  const [envoi, setEnvoi] = useState(false)

  const [code, setCode] = useState('')
  const [nom, setNom] = useState('')
  const [nomAr, setNomAr] = useState('')
  const [nomEn, setNomEn] = useState('')
  const [description, setDescription] = useState('')
  const [periode, setPeriode] = useState<'monthly' | 'yearly'>('yearly')
  const [prix, setPrix] = useState('')
  const [prixExploitation, setPrixExploitation] = useState('')
  const [maxExploitations, setMaxExploitations] = useState('')
  const [maxUtilisateurs, setMaxUtilisateurs] = useState('')
  const [maxParcelles, setMaxParcelles] = useState('')
  const [maxHectares, setMaxHectares] = useState('')
  const [essai, setEssai] = useState('0')
  const [publique, setPublique] = useState(false)
  const [ordre, setOrdre] = useState('100')
  const [inclus, setInclus] = useState('')
  const [modules, setModules] = useState<Set<string>>(new Set([MODULES[0]]))
  const [fonctions, setFonctions] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (token === '') return

    const controle = new AbortController()

    Promise.all([api.listPlatformFeatures(token, controle.signal), api.listPlatformPlans(token, controle.signal)])
      .then(([features, plans]) => {
        setCatalogue(features)

        if (creation) return

        const plan = plans.find((p) => String(p.id) === id) ?? null
        setExistante(plan)
        if (plan === null) return

        setCode(plan.code)
        setNom(plan.name)
        setNomAr(plan.nameAr ?? '')
        setNomEn(plan.nameEn ?? '')
        setDescription(plan.description ?? '')
        setPeriode(plan.billingPeriod)
        setPrix(texte(plan.price))
        setPrixExploitation(texte(plan.pricePerExtraFarm))
        setMaxExploitations(texte(plan.limits.maxFarms))
        setMaxUtilisateurs(texte(plan.limits.maxUsers))
        setMaxParcelles(texte(plan.limits.maxPlots))
        setMaxHectares(texte(plan.limits.maxAreaHectares))
        setEssai(texte(plan.trialDays))
        setPublique(plan.isPublic)
        setOrdre(texte(plan.displayOrder))
        setInclus(texte(plan.includedModuleCount))
        setModules(new Set(plan.modules))
        setFonctions(new Set(plan.features))
      })
      .catch((cause: unknown) => {
        if (!controle.signal.aborted) notify(describeError(cause), 'erreur')
      })
      .finally(() => {
        if (!controle.signal.aborted) setChargement(false)
      })

    return () => controle.abort()
  }, [token, id, creation, notify, describeError])

  const basculer = (setter: typeof setModules) => (valeur: string, coche: boolean) =>
    setter((actuels) => {
      const suivant = new Set(actuels)
      if (coche) suivant.add(valeur)
      else suivant.delete(valeur)
      return suivant
    })

  const optionsPeriode: SelectOption[] = [
    { value: 'yearly', label: t('platform.plans.yearly') },
    { value: 'monthly', label: t('platform.plans.monthly') },
  ]

  // « Toutes » = nul : le client ouvre toutes les activités proposées. Un nombre dit combien
  // il peut en choisir parmi elles — c'est ainsi qu'Essentiel donne UNE activité au choix.
  const optionsInclus: SelectOption[] = [
    { value: '', label: t('platform.plans.includedAll') },
    ...Array.from({ length: modules.size }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
  ]

  const enregistrer = async () => {
    const corps: PlanPayload = {
      code: creation ? code.trim() : null,
      name: nom.trim(),
      nameAr: nomAr.trim() === '' ? null : nomAr.trim(),
      nameEn: nomEn.trim() === '' ? null : nomEn.trim(),
      description: description.trim() === '' ? null : description.trim(),
      billingPeriod: periode,
      price: entierOuNul(prix) ?? 0,
      pricePerExtraFarm: entierOuNul(prixExploitation),
      maxFarms: entierOuNul(maxExploitations),
      maxUsers: entierOuNul(maxUtilisateurs),
      maxPlots: entierOuNul(maxParcelles),
      maxAreaHectares: entierOuNul(maxHectares),
      trialDays: entierOuNul(essai) ?? 0,
      isPublic: publique,
      displayOrder: entierOuNul(ordre) ?? 100,
      includedModuleCount: entierOuNul(inclus),
      modules: MODULES.filter((m) => modules.has(m)),
      features: catalogue.map((f) => f.code).filter((c) => fonctions.has(c)),
      rowVersion: existante?.rowVersion ?? null,
    }

    setEnvoi(true)

    try {
      if (creation) await api.createPlatformPlan(token, corps)
      else await api.updatePlatformPlan(token, Number(id), corps)

      notify(t(creation ? 'platform.plans.created' : 'platform.plans.saved'), 'succes')
      navigate('/platform/plans')
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  if (chargement) return <p className="text-texte-doux">{t('common.loading')}</p>

  if (!creation && existante === null) return <Alert tone="erreur">{t('platform.plan_not_found')}</Alert>

  const titre = creation ? t('platform.plans.new') : nomFormule(language, existante ?? { name: nom })
  const sansLimite = t('platform.plans.emptyMeansUnlimited')

  return (
    <Stack gap={4}>
      <PageHeader title={titre} description={t('platform.plans.editor.description')} />

      {!creation && existante !== null && existante.liveSubscriptions > 0 && (
        <Alert tone="info">
          {t('platform.plans.editor.grandfathered')} {existante.liveSubscriptions}
        </Alert>
      )}

      <Card title={t('platform.plans.group.identity')}>
        <Grid min="15rem">
          <TextField
            label={t('platform.plans.code')}
            value={code}
            onChange={setCode}
            hint={creation ? t('platform.plans.codeHint') : t('platform.plans.codeFrozen')}
            disabled={!creation}
            ltr
            required
          />
          <TextField label={t('platform.plans.nameFr')} value={nom} onChange={setNom} required />
          <TextField label={t('platform.plans.nameAr')} value={nomAr} onChange={setNomAr} />
          <TextField label={t('platform.plans.nameEn')} value={nomEn} onChange={setNomEn} ltr />
          <TextField label={t('platform.plans.descriptionField')} value={description} onChange={setDescription} />
        </Grid>
      </Card>

      <Card title={t('platform.plans.group.price')}>
        <Grid min="12rem">
          <SelectField
            label={t('platform.plans.period')}
            value={periode}
            options={optionsPeriode}
            onChange={(v) => setPeriode(v === 'monthly' ? 'monthly' : 'yearly')}
            required
          />
          <TextField label={t('platform.plans.priceField')} value={prix} onChange={setPrix} type="number" min="0" ltr required />
          <TextField
            label={t('platform.plans.extraFarmPrice')}
            value={prixExploitation}
            onChange={setPrixExploitation}
            type="number"
            min="0"
            ltr
          />
          <TextField label={t('platform.plans.trialDays')} value={essai} onChange={setEssai} type="number" min="0" max="365" ltr />
        </Grid>
      </Card>

      <Card title={t('platform.plans.group.limits')}>
        <Grid min="12rem">
          <TextField label={t('platform.plans.maxFarms')} value={maxExploitations} onChange={setMaxExploitations} type="number" min="1" hint={sansLimite} ltr />
          <TextField label={t('platform.plans.maxUsers')} value={maxUtilisateurs} onChange={setMaxUtilisateurs} type="number" min="1" hint={sansLimite} ltr />
          <TextField label={t('platform.plans.maxPlots')} value={maxParcelles} onChange={setMaxParcelles} type="number" min="1" hint={sansLimite} ltr />
          <TextField label={t('platform.plans.maxArea')} value={maxHectares} onChange={setMaxHectares} type="number" min="1" hint={sansLimite} ltr />
        </Grid>
      </Card>

      <Card title={t('platform.plans.group.content')}>
        <Grid min="15rem">
          <Stack gap={2}>
            <span className="text-[0.85rem] font-medium">{t('platform.clients.modules')}</span>
            {MODULES.map((m) => (
              <CheckboxField key={m} label={t(`module.${m}` as never)} checked={modules.has(m)} onChange={(c) => basculer(setModules)(m, c)} />
            ))}
            <SelectField label={t('platform.plans.includedModules')} value={inclus} options={optionsInclus} onChange={setInclus} />
          </Stack>

          <Stack gap={2}>
            <span className="text-[0.85rem] font-medium">{t('platform.plans.features')}</span>
            {catalogue.map((f) => (
              <CheckboxField
                key={f.code}
                label={t(`feature.${f.code}` as never)}
                checked={fonctions.has(f.code)}
                onChange={(c) => basculer(setFonctions)(f.code, c)}
              />
            ))}
            <p className="m-0 text-[0.8rem] text-texte-doux">{t('platform.plans.featuresHint')}</p>
          </Stack>
        </Grid>
      </Card>

      <Card title={t('platform.plans.group.visibility')}>
        <Stack gap={3}>
          <Grid min="15rem">
            <CheckboxField label={t('platform.plans.public')} checked={publique} onChange={setPublique} hint={t('platform.plans.publicHint')} />
            <TextField label={t('platform.plans.order')} value={ordre} onChange={setOrdre} type="number" ltr />
          </Grid>

          <FormActions>
            <Button onClick={() => void enregistrer()} pending={envoi} pendingLabel={t('common.loading')}>
              {creation ? t('common.create') : t('common.save')}
            </Button>
            <Button variant="secondaire" onClick={() => navigate('/platform/plans')} disabled={envoi}>
              {t('common.cancel')}
            </Button>
          </FormActions>
        </Stack>
      </Card>
    </Stack>
  )
}
